import React from 'react';

import { useCompleteModal } from 'modal.react.js';
import { CloudArrowUp, Trash } from 'phosphor-react';

import { Alert } from 'components/shared/Alert';
import { AModal } from 'components/shared/AModal';
import { IDropZoneFiles } from 'components/shared/Assets/shared/Form/types';
import Button from 'components/shared/Button';
import FileDropZone from 'components/shared/FileDropZone';
import Loader from 'components/shared/Loader';
import Text from 'components/shared/Typography/Text';
import { DOCUMENT_MIME_TYPES } from 'utils/asset';

import s from './MagicImportModal.module.css';

type Props = {
  onSubmit: (file: IDropZoneFiles, callback: () => void) => void;
};

export const MagicImportModal = (props: Props) => {
  const modalControl = useCompleteModal();
  const [isLoading, setIsLoading] = React.useState(false);
  const [file, setFile] = React.useState<IDropZoneFiles | null>(null);

  const closeModal = () => {
    modalControl.success(null);
  };

  const submitForm = async () => {
    try {
      setIsLoading(true);
      props.onSubmit(file!, () => {
        closeModal();
      });
    } catch (e) {
      console.error('---- submitForm e', e);
      setIsLoading(false);
    }
  };

  return (
    <AModal
      header="Magic Import"
      isOpen={true}
      footer={<MagicImportModalFooter isValid={file !== null} submitForm={submitForm} isLoading={isLoading} />}
      size="xlg"
      onClose={closeModal}
      isCloseOnEsc
      contentClassName={s.modalContent}
    >
      <div className={s.betaFeatureText}>
        <Text type="brandon" size="medium" className={s.betaFeature}>
          Beta Feature:&nbsp;
        </Text>
        <Text type="brandon" size="medium">
          We're still refining this and updates are on the way!
        </Text>
      </div>

      {file ? (
        <div className={s.fileWrapper}>
          <Text type="brandon" size="medium" className={s.fileName}>
            {file.filename}
          </Text>

          <Trash className={s.trashIcon} onClick={() => setFile(null)} />
        </div>
      ) : (
        <div className={s.dropZoneWrapper}>
          <FileDropZone
            placeholder="Drop your file or click to browse"
            allowedMimeTypes={DOCUMENT_MIME_TYPES}
            maxFileSize={10}
            recommendedFileSize={25}
            onSelectNewFiles={(files: IDropZoneFiles[]) => {
              if (!files?.[0]) return;

              setFile(files[0]);
            }}
            headComponent={<CloudArrowUp className={s.fileDropZoneIcon} />}
            needToShowWarning={false}
            multiple={false}
          />
        </div>
      )}

      <Alert
        variant="info"
        content={[
          'Once you select a file and click Import, the system will begin analyzing the file and inserting the content into your itinerary. This process usually takes 1–5 minutes.',
          'In the meantime, feel free to continue working in the itinerary builder — just make sure not to close the page until the import is complete.',
        ]}
        className={s.alert}
      />
    </AModal>
  );
};

const MagicImportModalFooter = (props: { isValid: boolean; submitForm: () => void; isLoading: boolean }) => {
  return (
    <div>
      <Button
        onClick={props.submitForm}
        disabled={!props.isValid || props.isLoading}
        type="primary"
        size="medium"
        htmlType="button"
      >
        <div className={s.submitButton}>
          <Text type="brandon" color="white">
            Submit
          </Text>
          {props.isLoading && <Loader width="1.6rem" height="1.6rem" />}
        </div>
      </Button>
    </div>
  );
};
